/**
 * How one view presents each of its fields.
 *
 * One endpoint, one cache entry, and a different shape per view type: a grid
 * stores a width and whether the column is shown, a form stores a label, a
 * description and whether the field is on the form at all. Nothing here knows
 * which of those it is holding, which is why every function is generic -- the
 * *plumbing* is shared and the *shape* belongs to the view type that reads it.
 */
import type { ViewFieldOptions } from '@/api/types'

/**
 * Reads the `{ field_options: { "12": {...} } }` wrapper the endpoint answers
 * with, whichever view type it came from.
 */
export function readFieldOptions<T>(
  response: ViewFieldOptions | undefined,
): Record<string, T> {
  return (
    (response as { field_options?: Record<string, T> } | undefined)?.field_options ?? {}
  )
}

/**
 * A view's options with some of them changed.
 *
 * Merged per field rather than replaced, because a change to one option -- a
 * width dragged, a column hidden, a label typed -- says nothing about the
 * others and must not clear them.
 */
export function mergeFieldOptions<T>(
  current: Record<string, T>,
  changes: Record<number, Partial<T>>,
): Record<string, T> {
  const next: Record<string, T> = { ...current }
  for (const [id, change] of Object.entries(changes)) {
    next[id] = { ...current[id], ...change } as T
  }
  return next
}

/** The same, in the wrapper the endpoint answers with, for writing to the cache. */
export function withFieldOptions<T>(
  response: ViewFieldOptions | undefined,
  changes: Record<number, Partial<T>>,
): ViewFieldOptions {
  return {
    field_options: mergeFieldOptions(readFieldOptions<T>(response), changes),
  } as ViewFieldOptions
}
